import * as THREE from 'three/webgpu';
import type {Viewer} from '../../viewer/Viewer';
import {ViewerMode} from '../../viewer/ViewerMode';
import {isFeatureCurve, isFeatureFace, isFeatureLine, isFeaturePoint, isFeatureSurface} from '../../mesh/feature';
import {isDatumAxis} from '../DatumAxis';
import {
  buildDatumReferenceEntityId,
  DatumReferenceEntityKind,
  parseDatumReferenceEntityId
} from '../DatumReferenceEntityId';
import {getDatumPlaneIdFromObject} from '../DatumPlane';
import {isDatumPoint} from '../DatumPoint';
import {
  DATUM_POINT_HISTORY_ENTITY_ID,
  type DatumPointFeatureHistoryRecord,
  type DatumPointHistoryDiagnostic,
  type DatumPointHistorySnapshot,
  type DatumPointHistoryVector3,
} from '../DatumPointHistoryTypes';
import {ReferenceCreationContext} from '../ReferenceCreationContext';
import {
  ReferenceCommandLifecycle,
  ReferenceCommandModeSource,
  ReferenceCoordinateFrame,
  ReferenceEntitySemanticType,
  ReferenceSelectionState,
  ReferenceSolveState,
} from '../ReferenceFeatureTypes';
import {
  DEFAULT_POINT_FEATURE_POLICIES,
  PointAlongCurveDistributionType,
  PointCurveDirectionPolicy,
  type PointEntityReference,
  type PointFeatureDefinition,
  type PointFeatureDiagnostic,
  type PointFeatureParams,
  type PointFeaturePolicies,
  type PointFeatureResult,
  PointFeatureStatus,
  PointGeometryMethod,
  PointIncludeEndpointPolicy,
  PointPickPolicy,
  PointPlacementDriver,
  type PointPlacementDriverValue,
  PointProjectionAmbiguityPolicy,
  ReferencePointSolver,
  type ResolvedEntityType,
} from './ReferencePointSolver';
import {convertMmToM} from '../../utils/unit';
import {resolveModelTopologyProxyObject} from '../../utils/feature/resolveModelTopologyOwnerResultMesh';
import {ReferencePointPreviewRenderer} from './ReferencePointPreviewRenderer';
import type {
  PointFeatureSolveGeometryData,
  PointFeatureSolveRuntimeCandidate,
} from '../tool/PointFeatureSolveGeometryPayloadTypes';
import {resolveCircularEdgePayload} from '../tool/ReferencePointCircularEdgeUtils';
import {normalizeLinearEndpointsDirection} from '../tool/ReferencePointLinearDirectionUtils';
import {
  resolveImportedAssemblyTopologySelectionRef
} from '../../imported-model/selection/resolveImportedAssemblyTopologySelectionRef';
import {resolveTopologySelectionPick} from '../../utils/feature/resolveTopologySelectionPick';
import {resolveAssemblyTopologyReferenceObject} from '../shared/resolveAssemblyTopologyReferenceObject';

/**
 * 基准点选择候选。
 */
export type PointSelectionCandidate = {
  /** 实体 ID。 */
  entityId: string;
  /** 实体类型。 */
  entityType: ResolvedEntityType;
  /** 展示标签。 */
  label: string;
  /** 扩展几何载荷。 */
  payload?: PointFeatureSolveGeometryData;
};

/**
 * 基准点选择拓扑载荷。
 */
interface PointSelectionTopologyPayload {
  /** 装配体导入拓扑稳定引用。 */
  assemblyTopologyRef?: AssemblyTopologySelectionRef | null;

    /** 选择层解析出的拓扑命中快照 */
  topologyPick?: ResolvedTopologyPick | null;

}


/**
 * 构建装配体拓扑选择载荷。
 *
 * @param object 当前选择对象。
 * @returns 拓扑选择载荷。
 */
function buildPointSelectionTopologyPayload(object: THREE.Object3D): PointSelectionTopologyPayload {
  const topologyPick = resolveTopologySelectionPick(object);
  return {
    assemblyTopologyRef: resolveImportedAssemblyTopologySelectionRef(object, topologyPick),

      topologyPick,

  };
}

/**
 * 从候选载荷读取装配体拓扑稳定引用。
 *
 * @param payload 候选载荷。
 * @returns 稳定拓扑引用；不存在时返回 `null`。
 */
function readPointSelectionAssemblyTopologyRef(
  payload: PointFeatureSolveGeometryData | undefined,
): AssemblyTopologySelectionRef | null {
  const assemblyTopologyRef = payload?.assemblyTopologyRef;
  return assemblyTopologyRef ?? null;
}


/**
 * 从候选载荷读取拓扑命中快照。
 *
 * @param payload 候选载荷。
 * @returns 拓扑命中快照；不存在时返回 `null`。
 */
function readPointSelectionTopologyPick(
  payload: PointFeatureSolveGeometryData | undefined,
): ResolvedTopologyPick | null {
  const topologyPick = (payload as PointSelectionTopologyPayload | undefined)?.topologyPick;
  return topologyPick ?? null;
}


/**
 * 槽位选择映射。
 */
export type SlotSelectionMap = Record<string, PointSelectionCandidate[]>;

/**
 * 选择槽位契约。
 */
export interface SelectionSlotContract {
  /** 槽位 ID。 */
  slotId: string;
  /** 是否必填。 */
  required: boolean;
  /** 允许接收的实体类型。 */
  allowedTypes: ResolvedEntityType[];
  /** 最大接收数量。 */
  maxCount: number;
  /** 与现有选择的兼容性判断。 */
  compatibleWith?: (existing: SlotSelectionMap) => boolean;
}

/**
 * 基准点命令选择契约。
 */
export interface PointCommandSelectionContract {
  /** 对应几何方法。 */
  method: PointGeometryMethod;
  /** 该方法所需槽位集合。 */
  slots: SelectionSlotContract[];
}

/**
 * 基准点命令状态。
 */
export interface PointCommandState {
  /** 生命周期。 */
  lifecycle: ReferenceCommandLifecycle;
  /** 放置驱动类型。 */
  placementDriver: PointPlacementDriver;
  /** 模式来源。 */
  modeSource: ReferenceCommandModeSource;
  /** 当前几何方法。 */
  geometryMethod: PointGeometryMethod | null;
  /** 选择完整度状态。 */
  selectionState: ReferenceSelectionState;
  /** 求解状态。 */
  solveState: ReferenceSolveState;
  /** 当前槽位选择。 */
  selections: SlotSelectionMap;
  /** 当前参数。 */
  params: PointFeatureParams;
  /** 当前预览结果。 */
  preview?: PointFeatureResult;
  /** 是否已修改。 */
  dirty: boolean;
}

/**
 * 基准点控制器配置。
 */
export interface ReferencePointControllerOptions {
  /** 文档 ID。 */
  documentId: string;
  /** 坐标系类型。 */
  coordinateFrame?: ReferenceCoordinateFrame;
  /** 单位制。 */
  unitSystem?: string;
  /** 策略覆盖。 */
  policies?: Partial<PointFeaturePolicies>;
  /** 提交成功回调。 */
  onCommitted?: (feature: PointFeatureDefinition, result: PointFeatureResult) => void;
  /** 取消回调。 */
  onCancelled?: () => void;
}

/**
 * 基准点特征记录。
 */
export interface PointFeatureRecord {
  /** 特征 ID。 */
  featureId: string;
  /** 正式对象创建来源上下文。 */
  creationContext: ReferenceCreationContext;
  /** 特征定义。 */
  definition: PointFeatureDefinition;
  /** 最近一次求解结果。 */
  resultCache: PointFeatureResult;
  /** 重建诊断信息。 */
  rebuildDiagnostics: PointFeatureResult['diagnostics'];
  /** 是否可见。 */
  visible: boolean;
  /** 当前点集合缓存。 */
  points: THREE.Vector3[];
  /** 已创建的正式基准点 ID 列表。 */
  datumPointIds?: number[];
}

/**
 * 基准点控制器视图模型。
 */
export interface PointCommandViewModel {
  /** 当前命令状态。 */
  state: PointCommandState;
  /** 当前生效策略。 */
  policies: PointFeaturePolicies;
  /** 是否可提交。 */
  canSubmit: boolean;
  /** 选择摘要。 */
  selectionSummary: Array<{ slotId: string; values: Array<{ label: string; entityId: string }> }>;
  /** 已创建特征记录。 */
  featureRecords: PointFeatureRecord[];
}


/**
 * 基准点编辑会话中单个正式点的状态快照。
 */
interface ReferencePointEditSessionPointState {
  /** 正式基准点 ID。 */
  pointId: number;
  /** 编辑前名称。 */
  pointName: string;
  /** 编辑前可见性。 */
  previousVisible: boolean;
}

/**
 * 基准点编辑特征会话。
 */
interface ReferencePointEditSession {
  /** 正在编辑的特征 ID。 */
  featureId: string;
  /** 编辑前正式点状态快照。 */
  pointStates: ReferencePointEditSessionPointState[];
}


/**
 * 创建空命令状态。
 */
export function createEmptyCommandState(): PointCommandState {
  return {
    lifecycle: ReferenceCommandLifecycle.Closed,
    placementDriver: PointPlacementDriver.Geometry,
    modeSource: ReferenceCommandModeSource.AutoInferred,
    geometryMethod: null,
    selectionState: ReferenceSelectionState.Empty,
    solveState: ReferenceSolveState.Idle,
    selections: {},
    params: {
      xyz: { x: 0, y: 0, z: 0 },
      alongCurve: {
        distributionType: PointAlongCurveDistributionType.Distance,
        pointCount: 1,
        spacingValue: convertMmToM(10),
        includeEndpointPolicy: PointIncludeEndpointPolicy.None,
        directionPolicy: PointCurveDirectionPolicy.Natural,
        curveEvaluationTolerance: 1e-6,
      },
      intersection: {
        pickPolicy: PointPickPolicy.First,
      },
      projection: {
        ambiguityPolicy: PointProjectionAmbiguityPolicy.Fail,
      },
    },
    dirty: false,
  };
}

/**
 * 根据 Viewer 模式解析正式参考对象创建来源。
 *
 * @param mode 当前 Viewer 模式。
 * @returns 创建来源上下文。
 */
function resolveReferenceCreationContextFromViewerMode(mode: ViewerMode): ReferenceCreationContext {
  return (mode === ViewerMode.Assembly || mode === ViewerMode.MoveComponent)
    ? ReferenceCreationContext.Assembly
    : ReferenceCreationContext.PartFeature;
}


/**
 * 根据正式参考对象创建来源解析命令结束后的宿主模式。
 *
 * @param context 正式对象创建来源。
 * @returns 命令结束后应恢复的 Viewer 模式。
 */
function resolveReferenceCommandExitViewerMode(context: ReferenceCreationContext): ViewerMode {
  return context === ReferenceCreationContext.Assembly
    ? ViewerMode.Assembly
    : ViewerMode.Default;
}


/**
 * 选择契约集合。
 */
const POINT_SELECTION_CONTRACTS: PointCommandSelectionContract[] = [
  {
    method: PointGeometryMethod.CircularCenter,
    slots: [{ slotId: 'primary', required: true, allowedTypes: [ReferenceEntitySemanticType.FeatureCurve], maxCount: 1 }],
  },

    {
    method: PointGeometryMethod.CenterOfFace,
    slots: [{ slotId: 'face', required: true, allowedTypes: [ReferenceEntitySemanticType.FeatureFace, ReferenceEntitySemanticType.FeatureSurface], maxCount: 1 }],
  },

    {
    method: PointGeometryMethod.Intersection,
    slots: [
      {
        slotId: 'entityA',
        required: true,
        allowedTypes: [
          ReferenceEntitySemanticType.FeatureCurve,
          ReferenceEntitySemanticType.FeatureLine,
          ReferenceEntitySemanticType.DatumAxis,
          ReferenceEntitySemanticType.DatumPlane,
          ReferenceEntitySemanticType.FeatureSurface,
        ],
        maxCount: 1,
      },
      {
        slotId: 'entityB',
        required: true,
        allowedTypes: [
          ReferenceEntitySemanticType.FeatureCurve,
          ReferenceEntitySemanticType.FeatureLine,
          ReferenceEntitySemanticType.DatumAxis,
          ReferenceEntitySemanticType.DatumPlane,
          ReferenceEntitySemanticType.FeatureSurface,
        ],
        maxCount: 1,
      },
    ],
  },
  {
    method: PointGeometryMethod.Projection,
    slots: [
      { slotId: 'source', required: true, allowedTypes: [ReferenceEntitySemanticType.FeaturePoint, ReferenceEntitySemanticType.DatumPoint], maxCount: 1 },
      { slotId: 'target', required: true, allowedTypes: [ReferenceEntitySemanticType.DatumPlane, ReferenceEntitySemanticType.FeatureFace, ReferenceEntitySemanticType.FeatureSurface], maxCount: 1 },
    ],
  },
  {
    method: PointGeometryMethod.OnPoint,
    slots: [{ slotId: 'point', required: true, allowedTypes: [ReferenceEntitySemanticType.FeaturePoint, ReferenceEntitySemanticType.DatumPoint], maxCount: 1 }],
  },
  {
    method: PointGeometryMethod.AlongCurve,

      slots: [{ slotId: 'curve', required: true, allowedTypes: [ReferenceEntitySemanticType.FeatureLine, ReferenceEntitySemanticType.FeatureCurve, ReferenceEntitySemanticType.DatumAxis], maxCount: 1 }],

  },
];

/**
 * 获取选择契约。
 */
function getContract(method: PointGeometryMethod | null): PointCommandSelectionContract | null {
  if (!method) {
    return null;
  }
  return POINT_SELECTION_CONTRACTS.find((item) => item.method === method) ?? null;
}

/**
 * 推断几何方法映射。
 */
/**
 * 推断几何方法。
 */
function inferPointGeometryMethod(candidate: PointSelectionCandidate | null): PointGeometryMethod | null {
  if (!candidate) {
    return null;
  }
  if (candidate.entityType === ReferenceEntitySemanticType.FeatureFace || candidate.entityType === ReferenceEntitySemanticType.FeatureSurface) {
    return PointGeometryMethod.CenterOfFace;
  }
  if (candidate.entityType === ReferenceEntitySemanticType.DatumPlane) {
    return PointGeometryMethod.Projection;
  }
  if (candidate.entityType === ReferenceEntitySemanticType.FeaturePoint || candidate.entityType === ReferenceEntitySemanticType.DatumPoint) {
    return PointGeometryMethod.OnPoint;
  }
  if (candidate.entityType === ReferenceEntitySemanticType.FeatureCurve) {
    return candidate.payload?.center ? PointGeometryMethod.CircularCenter : PointGeometryMethod.AlongCurve;
  }
  if (candidate.entityType === ReferenceEntitySemanticType.FeatureLine || candidate.entityType === ReferenceEntitySemanticType.DatumAxis) {
    return PointGeometryMethod.AlongCurve;
  }
  return null;
}

/**
 * 归一化策略。
 */
function normalizePolicies(input?: Partial<PointFeaturePolicies>): PointFeaturePolicies {
  return {
    ...DEFAULT_POINT_FEATURE_POLICIES,
    ...(input ?? {}),
  };
}

/**
 * 克隆参数。
 */
function cloneParams(params: PointFeatureParams): PointFeatureParams {
  return JSON.parse(JSON.stringify(params)) as PointFeatureParams;
}

/**
 * 清理不兼容选择。
 */
function clearIncompatibleSelections(
  selections: SlotSelectionMap,
  method: PointFeatureDefinition['geometryMethod'],
): SlotSelectionMap {
  const contract = getContract(method);
  if (!contract) {
    return {};
  }
  const allowedSlots = new Set(contract.slots.map((slot) => slot.slotId));
  return Object.fromEntries(Object.entries(selections).filter(([slotId]) => allowedSlots.has(slotId)));
}

/**
 * 构建几何引用。
 */
function buildGeometryReferences(
  selections: SlotSelectionMap,
  method: PointFeatureDefinition['geometryMethod'],
): PointEntityReference[] {
  const references: PointEntityReference[] = [];
  const contract = getContract(method);
  const orderedSlotIds = contract?.slots.map((slot) => slot.slotId) ?? Object.keys(selections);
  const visitedSlotIdSet = new Set<string>();

  orderedSlotIds.forEach((slotId) => {
    const values = selections[slotId] ?? [];
    visitedSlotIdSet.add(slotId);
    values.forEach((item) => {
      references.push({
        entityId: item.entityId,
        entityType: item.entityType,
        slot: slotId,
        assemblyTopologyRef: readPointSelectionAssemblyTopologyRef(item.payload),

          // 保存拓扑命中快照，供特征重建后重算使用
        topologyPick: readPointSelectionTopologyPick(item.payload),

      });
    });
  });

  Object.entries(selections).forEach(([slotId, values]) => {
    if (visitedSlotIdSet.has(slotId)) {
      return;
    }
    values.forEach((item) => {
      references.push({
        entityId: item.entityId,
        entityType: item.entityType,
        slot: slotId,
        assemblyTopologyRef: readPointSelectionAssemblyTopologyRef(item.payload),

          // 保存拓扑命中快照，供特征重建后重算使用
        topologyPick: readPointSelectionTopologyPick(item.payload),

      });
    });
  });
  return references;
}

/**
 * 更新选择状态。
 */
function updateSelectionState(state: PointCommandState): PointCommandState['selectionState'] {
  if (state.placementDriver === PointPlacementDriver.NumericXYZ) {
    return ReferenceSelectionState.Complete;
  }

  const contract = getContract(state.geometryMethod);
  if (!contract) {
    return ReferenceSelectionState.Empty;
  }

  let selectedAny = false;
  for (const slot of contract.slots) {
    const picked = state.selections[slot.slotId] ?? [];
    if (picked.length > 0) {
      selectedAny = true;
    }
    const hasInvalid = picked.some((item) => !slot.allowedTypes.includes(item.entityType));
    if (hasInvalid || picked.length > slot.maxCount) {
      return ReferenceSelectionState.Invalid;
    }
    if (slot.required && picked.length === 0) {
      return selectedAny ? ReferenceSelectionState.Partial : ReferenceSelectionState.Empty;
    }
  }
  return selectedAny ? ReferenceSelectionState.Complete : ReferenceSelectionState.Empty;
}

/**
 * 参考点控制器。
 *
 * 设计说明：
 * 1. 将原 `commands` 目录中的状态、选择规则、动作函数统一内聚到本类与本文件；
 * 2. 控制器在构造时持有 `ReferencePointSolver` 实例，预览和重建直接使用实例求解；
 * 3. 对外只保留当前文件与 `ReferencePointSolver.ts` 两个核心类文件。
 */
export class ReferencePointController {
  private readonly viewer: Viewer;
  private readonly options: ReferencePointControllerOptions;
  private readonly policies: PointFeaturePolicies;
  private readonly solver: ReferencePointSolver;

    /** 基准点预览渲染器。 */
  private readonly previewRenderer: ReferencePointPreviewRenderer;

    private state: PointCommandState = createEmptyCommandState();
  private records: PointFeatureRecord[] = [];

    /** 当前基准点编辑特征会话；为空表示创建模式。 */
  private editSession: ReferencePointEditSession | null = null;

    /** 当前求解过程的选择作用域。 */
  private solvingSelectionScope: SlotSelectionMap | null = null;
  /** 当前命令会话的正式对象创建来源。 */
  private pendingCreationContext: ReferenceCreationContext = ReferenceCreationContext.PartFeature;
  private lastAlongCurveParams: PointCommandState['params']['alongCurve'] | null = null;
  private static readonly XYZ_SYNC_EPSILON = 1e-9;
  private static readonly ALONG_CURVE_DISTANCE_DEFAULT = convertMmToM(10);

  /**
   * 创建基准点控制器。
   *
   * @param viewer 视图实例。
   * @param options 控制器配置。
   */
  public constructor(viewer: Viewer, options: ReferencePointControllerOptions) {
    this.viewer = viewer;
    this.options = options;
    this.policies = normalizePolicies(options.policies ?? DEFAULT_POINT_FEATURE_POLICIES);
    this.solver = new ReferencePointSolver(this);

      this.previewRenderer = new ReferencePointPreviewRenderer(viewer);

  }

  /**
   * 获取当前视图模型。
   *
   * @returns 当前命令可视状态与记录集合。
   */
  public getViewModel(): PointCommandViewModel {
    const canSubmit = this.state.solveState === ReferenceSolveState.Previewable && this.state.preview?.status === PointFeatureStatus.Ready;
    const flatEntries = Object.entries(this.state.selections).flatMap(([slotId, values]) => (
      values.map((item) => ({ slotId, label: item.label, entityId: item.entityId }))
    ));
    const cappedEntries = flatEntries.length > 2 ? flatEntries.slice(flatEntries.length - 2) : flatEntries;
    const groupedSummary = new Map<string, Array<{ label: string; entityId: string }>>();
    cappedEntries.forEach((entry) => {
      const list = groupedSummary.get(entry.slotId) ?? [];
      list.push({ label: entry.label, entityId: entry.entityId });
      groupedSummary.set(entry.slotId, list);
    });
    const selectionSummary = Array.from(groupedSummary.entries()).map(([slotId, values]) => ({ slotId, values }));

    // 视图模型只暴露最后两个选择摘要，避免属性面板中信息冗余。
    return {
      state: this.state,
      policies: this.policies,
      canSubmit,
      selectionSummary,
      featureRecords: this.records,
    };
  }


    /**
   * 判断当前命令是否为编辑特征会话。
   *
   * @returns 编辑特征会话返回 `true`。
   */
  public isEditingFeature(): boolean {
    return this.editSession !== null;
  }

  /**
   * 判断指定正式基准点是否支持编辑特征。
   *
   * @param pointId 正式基准点 ID。
   * @returns 命中可复算特征记录时返回 `true`。
   */
  public canEditFeatureByPointId(pointId: number): boolean {
    return this.records.some((record) => (record.datumPointIds ?? []).includes(pointId));
  }

  /**
   * 规范化基准点编辑提交前的历史快照。
   *
   * 设计说明：
   * 1. 编辑态会临时隐藏原正式基准点，避免与预览点重叠；
   * 2. 若 before 快照直接采集该临时状态，Undo 会回到隐藏态；
   * 3. 因此提交历史前需要把目标点可见性回填为进入编辑前状态。
   *
   * @param snapshot 提交前采集的基准点历史快照。
   * @returns 已归一化的历史快照。
   */
  public normalizeHistorySnapshotBeforeSubmit(snapshot: DatumPointHistorySnapshot): DatumPointHistorySnapshot {
    const activeEditSession = this.editSession;
    if (!activeEditSession) {
      return snapshot;
    }

    const visibilityByPointId = new Map<number, boolean>();
    activeEditSession.pointStates.forEach((pointState) => {
      visibilityByPointId.set(pointState.pointId, pointState.previousVisible);
    });

    return {
      ...snapshot,
      points: (snapshot.points ?? []).map((pointRecord) => (
        visibilityByPointId.has(pointRecord.pointId)
          ? {
            ...pointRecord,
            visible: visibilityByPointId.get(pointRecord.pointId) === true,
          }
          : pointRecord
      )),
    };
  }


    /**
   * 清空当前所有选择。
   */
  public clearSelections(): void {
    this.state.selections = {};
    this.state.dirty = true;
    this.recomputePreview();
  }

  /**
   * 移除指定实体选择。
   *
   * @param entityId 实体 ID。
   */
  public removeSelection(entityId: string): void {
    const newSelections: PointCommandState['selections'] = {};
    let changed = false;
    Object.entries(this.state.selections).forEach(([slotId, list]) => {
      const filtered = list.filter((item) => item.entityId !== entityId);
      if (filtered.length !== list.length) {
        changed = true;
      }
      if (filtered.length > 0) {
        newSelections[slotId] = filtered;
      }
    });
    if (!changed) {
      return;
    }

    this.state.selections = newSelections;
    // 删除引用后需要重新评估模式回退，避免保留已不成立的构造方式。
    this.applyModeFallbackAfterDeletion();
    this.state.dirty = true;
    this.recomputePreview();
  }

  /**
   * 打开基准点命令。
   *
   * @param preselected 可选预选对象。
   */
  public open(preselected: PointSelectionCandidate | null = null): void {
    this.restoreEditingPointVisibilityIfNeeded();
    this.editSession = null;
    this.pendingCreationContext = resolveReferenceCreationContextFromViewerMode(this.viewer.getMode());
    this.state = {
      ...createEmptyCommandState(),
      lifecycle: ReferenceCommandLifecycle.Open,
      dirty: false,
    };

    if (preselected) {
      // 打开命令时优先根据预选对象自动推断方法，降低首次操作成本。
      const inferred = inferPointGeometryMethod(preselected);
      if (inferred) {
        this.state.geometryMethod = inferred;
        this.state.modeSource = ReferenceCommandModeSource.AutoInferred;
        this.insertSelection(preselected);
      }
    }

    this.recomputePreview();
    this.viewer.entitySelectionManager?.clearSelectionProgrammatically();
    this.viewer.setMode('reference-point' as never);
    this.viewer.requestRender();
  }


    /**
   * 打开已有基准点的编辑特征会话。
   *
   * 设计说明：
   * 1. 设计树入口只传正式基准点 ID，控制器必须反向定位所属特征记录；
   * 2. 编辑参数恢复必须来自 `PointFeatureRecord.definition`，不从当前几何反推；
   * 3. 进入编辑态时临时隐藏对应正式点，防止与预览重叠。
   *
   * @param pointId 待编辑的正式基准点 ID。
   * @returns 打开结果。
   */
  public openEdit(pointId: number): { ok: boolean; reason?: string } {
    const targetRecord = this.records.find((record) => (record.datumPointIds ?? []).includes(pointId));
    if (!targetRecord) {
      return { ok: false, reason: '默认或导入基准点不支持编辑特征' };
    }

    const unresolvedReference = targetRecord.definition.references.find((reference) => (
      !this.resolveRestoredPointSelectionCandidate(reference)
    ));
    if (unresolvedReference) {
      return { ok: false, reason: '基准点编辑特征参数恢复失败，存在失效引用' };
    }

    const restoredPointStates: ReferencePointEditSessionPointState[] = (targetRecord.datumPointIds ?? [])
      .map((datumPointId) => {
        const datumPoint = this.viewer.getDatumPoint(datumPointId);
        if (!datumPoint) {
          return null;
        }
        return {
          pointId: datumPoint.datumPointId,
          pointName: datumPoint.name,
          previousVisible: datumPoint.isVisible(),
        };
      })
      .filter((pointState): pointState is ReferencePointEditSessionPointState => pointState !== null);
    if (restoredPointStates.length <= 0) {
      return { ok: false, reason: '未找到目标基准点，无法编辑' };
    }

    this.restoreEditingPointVisibilityIfNeeded();
    this.pendingCreationContext = targetRecord.creationContext;
    this.editSession = {
      featureId: targetRecord.featureId,
      pointStates: restoredPointStates,
    };
    this.state = {
      ...createEmptyCommandState(),
      lifecycle: ReferenceCommandLifecycle.Open,
      placementDriver: targetRecord.definition.placementDriver,
      geometryMethod: targetRecord.definition.geometryMethod,
      modeSource: ReferenceCommandModeSource.ManualLocked,
      params: cloneParams(targetRecord.definition.params),
      selections: this.rehydrateSelections(targetRecord.definition),
      dirty: false,
    };
    restoredPointStates.forEach((pointState) => {
      this.viewer.getDatumPoint(pointState.pointId)?.setVisible(false);
    });
    this.recomputePreview();
    this.syncSelectionVisuals();
    this.viewer.setMode('reference-point' as never);
    this.viewer.requestRender();
    return { ok: true };
  }


    /**
   * 关闭命令并清空状态。
   */
  public close(): void {

        this.clearPreview();

        this.state = createEmptyCommandState();
    this.pendingCreationContext = ReferenceCreationContext.PartFeature;
    this.viewer.requestRender();
  }

  /**
   * 取消当前命令。
   */
  public cancel(): void {
    this.restoreEditingPointVisibilityIfNeeded();
    this.editSession = null;

      const exitViewerMode = resolveReferenceCommandExitViewerMode(this.pendingCreationContext);

      this.close();
    this.options.onCancelled?.();
    this.viewer.entitySelectionManager?.clearSelectionProgrammatically();
    this.viewer.setMode(exitViewerMode as never);
    this.viewer.requestRender();
  }


    /**
   * 释放控制器资源。
   */
  public dispose(): void {
    this.previewRenderer.dispose();
  }


    /**
   * 重置模式锁定状态。
   */
  public resetModeLock(): void {
    this.state.modeSource = ReferenceCommandModeSource.AutoInferred;
  }

  /**
   * 设置放置驱动类型。
   *
   * @param driver 目标驱动类型。
   */
  public setPlacementDriver(driver: PointPlacementDriver | PointPlacementDriverValue): void {
    const normalizedDriver = driver === PointPlacementDriver.NumericXYZ
      ? PointPlacementDriver.NumericXYZ
      : PointPlacementDriver.Geometry;
    const previousDriver = this.state.placementDriver;
    const previewPoint = this.state.preview?.points?.[0];
    // 从几何模式切到数值模式时，尽量把当前预览点坐标回填给数值输入。
    if (normalizedDriver === PointPlacementDriver.NumericXYZ && previousDriver !== PointPlacementDriver.NumericXYZ && previewPoint instanceof THREE.Vector3) {
      this.state.params = {
        ...this.state.params,
        xyz: {
          x: previewPoint.x,
          y: previewPoint.y,
          z: previewPoint.z,
        },
      };
    }

    this.state.placementDriver = normalizedDriver;
    this.state.dirty = true;
    if (normalizedDriver === PointPlacementDriver.NumericXYZ) {
      this.state.solveState = ReferenceSolveState.Idle;
    }
    this.recomputePreview();
  }

  /**
   * 设置当前几何方法。
   *
   * @param method 目标几何方法。
   */
  public setGeometryMethod(method: PointGeometryMethod): void {
    const changed = this.state.geometryMethod !== method;
    const previousSelections = { ...this.state.selections };
    this.state.geometryMethod = method;
    this.state.modeSource = ReferenceCommandModeSource.ManualLocked;
    if (method !== PointGeometryMethod.Intersection) {
      this.lastAlongCurveParams = null;
    }

    if (changed && !this.policies.retainCompatibleSelectionsOnModeSwitch) {
      this.state.selections = {};
    } else if (changed) {
      // 只保留与新方法兼容的槽位，避免把旧模式残留引用带入新求解。
      this.state.selections = clearIncompatibleSelections(this.state.selections, method);
    }

    if (changed && method === PointGeometryMethod.AlongCurve) {
      try {
        const previousPrimary = previousSelections.primary?.[0];
        if (previousPrimary) {
          const contract = getContract(PointGeometryMethod.AlongCurve);
          const curveSlot = contract?.slots.find((slot) => slot.slotId === 'curve');
          if (curveSlot && curveSlot.allowedTypes.includes(previousPrimary.entityType)) {
            this.state.selections = {
              ...this.state.selections,
              curve: [previousPrimary],
            };
          }
        }
      } catch {
        // 保持模式切换主流程稳定，不在迁移失败时中断。
      }
    }

    this.state.dirty = true;
    this.recomputePreview();
  }

  /**
   * 清空当前几何方法。
   */
  public clearGeometryMethod(): void {
    this.state.geometryMethod = null;
    this.state.modeSource = ReferenceCommandModeSource.AutoInferred;
    this.state.dirty = true;
    this.recomputePreview();
  }

  /**
   * 合并更新当前参数。
   *
   * @param patch 参数补丁。
   */
  public setParams(patch: Partial<PointCommandState['params']>): void {
    this.state.params = {
      ...this.state.params,
      ...patch,
    };
    this.state.dirty = true;
    this.recomputePreview();
  }

  /**
   * 处理一次新的实体选择。
   *
   * @param candidate 选择候选。
   * @returns 是否接收本次选择。
   */
  public handleSelection(candidate: PointSelectionCandidate): boolean {
    if (this.state.lifecycle !== ReferenceCommandLifecycle.Open || this.state.placementDriver === PointPlacementDriver.NumericXYZ) {
      return false;
    }

    if (this.hasSelectionCandidate(candidate.entityId)) {
      this.removeSelection(candidate.entityId);
      return true;
    }

    // 点性对象优先触发 On Point 自动模式，保证点到点操作符合预期。
    if (this.isPointSelectionCandidate(candidate)) {
      const inferred = inferPointGeometryMethod(candidate);
      if (
        inferred === PointGeometryMethod.OnPoint
        && this.state.geometryMethod !== PointGeometryMethod.OnPoint
        && !this.shouldPreferProjectionOverOnPoint(candidate)
      ) {
        this.state.geometryMethod = PointGeometryMethod.OnPoint;
        this.state.modeSource = ReferenceCommandModeSource.AutoInferred;
        if (!this.policies.retainCompatibleSelectionsOnModeSwitch) {
          this.state.selections = {};
        } else {
          this.state.selections = clearIncompatibleSelections(this.state.selections, PointGeometryMethod.OnPoint);
        }
      }
    }

    if (!this.state.geometryMethod) {
      // 没有显式方法时，首次选择直接作为模式推断来源。
      const inferred = inferPointGeometryMethod(candidate);
      if (inferred) {
        this.state.geometryMethod = inferred;
        this.state.modeSource = ReferenceCommandModeSource.AutoInferred;
        if (!this.policies.retainCompatibleSelectionsOnModeSwitch) {
          this.state.selections = {};
        } else {
          this.state.selections = clearIncompatibleSelections(this.state.selections, inferred);
        }
        if (inferred === PointGeometryMethod.AlongCurve) {
          this.state.params = {
            ...this.state.params,
            alongCurve: {
              ...this.state.params.alongCurve,
              distributionType: PointAlongCurveDistributionType.Distance,
              spacingValue: ReferencePointController.ALONG_CURVE_DISTANCE_DEFAULT,
              pointCount: 1,
            },
          };
        }
      }
    }

    if (this.tryAutoTransitionToProjection(candidate)) {
      this.state.dirty = true;
      this.recomputePreview();
      return true;
    }

    if (this.state.modeSource !== ReferenceCommandModeSource.ManualLocked && this.state.geometryMethod) {
      try {
        const contract = getContract(this.state.geometryMethod);
        if (contract) {
          for (const slot of contract.slots) {
            const current = this.state.selections[slot.slotId] ?? [];
            if (slot.allowedTypes.includes(candidate.entityType) && current.length < slot.maxCount) {
              this.state.selections = {
                ...this.state.selections,
                [slot.slotId]: [...current, candidate],
              };
              this.state.dirty = true;
              this.recomputePreview();
              return true;
            }
          }
        }
      } catch {
        // 出错时回退到正常模式迁移路径。
      }

      if (this.tryAutoModeTransition(candidate)) {
        this.state.dirty = true;
        this.recomputePreview();
        return true;
      }

      if (!this.shouldPreserveIntersectionModeOnSelection(candidate)) {
        const inferred = inferPointGeometryMethod(candidate);
        if (
          inferred
          && inferred !== this.state.geometryMethod
          && !(inferred === PointGeometryMethod.OnPoint && this.shouldPreferProjectionOverOnPoint(candidate))
        ) {
          this.state.geometryMethod = inferred;
          this.state.modeSource = ReferenceCommandModeSource.AutoInferred;
          if (!this.policies.retainCompatibleSelectionsOnModeSwitch) {
            this.state.selections = {};
          } else {
            this.state.selections = clearIncompatibleSelections(this.state.selections, inferred);
          }
          if (inferred === PointGeometryMethod.AlongCurve) {
            this.state.params = {
              ...this.state.params,
              alongCurve: {
                ...this.state.params.alongCurve,
                distributionType: PointAlongCurveDistributionType.Distance,
                spacingValue: ReferencePointController.ALONG_CURVE_DISTANCE_DEFAULT,
                pointCount: 1,
              },
            };
          }
        }
      }
    }

    const accepted = this.insertSelection(candidate);
    if (!accepted) {
      this.state.selectionState = ReferenceSelectionState.Invalid;
      this.state.solveState = ReferenceSolveState.Error;
      return false;
    }

    this.state.dirty = true;
    this.recomputePreview();
    return true;
  }

  /**
   * 提交当前预览为正式基准点特征。
   *
   * @returns 提交结果。
   */
  public submit(): { ok: boolean; featureId?: string; reason?: string } {
    const viewModel = this.getViewModel();
    if (!viewModel.canSubmit || !this.state.preview) {
      return { ok: false, reason: '当前状态不可提交' };
    }

    const activeEditSession = this.editSession;
    const featureId = activeEditSession?.featureId ?? this.createPointFeatureId();

      const exitViewerMode = resolveReferenceCommandExitViewerMode(this.pendingCreationContext);

      const definition = this.buildDefinition(featureId);
    if (activeEditSession) {
      const editableRecord = this.records.find((record) => record.featureId === featureId);
      if (!editableRecord) {
        return { ok: false, reason: '基准点编辑提交失败，原特征记录不存在' };
      }
      editableRecord.creationContext = this.pendingCreationContext;
      editableRecord.definition = definition;
      editableRecord.resultCache = this.state.preview;
      editableRecord.rebuildDiagnostics = this.state.preview.diagnostics;
      editableRecord.points = this.state.preview.points;
      const updated = this.updateDatumPointsFromPreviewForEdit(editableRecord, activeEditSession.pointStates);
      if (!updated) {
        return { ok: false, reason: '基准点编辑提交失败，请检查目标基准点是否仍存在' };
      }
    } else {
      const record: PointFeatureRecord = {
        featureId,
        creationContext: this.pendingCreationContext,
        definition,
        resultCache: this.state.preview,
        rebuildDiagnostics: this.state.preview.diagnostics,
        visible: true,
        points: this.state.preview.points,
      };

      this.records.push(record);
      // 先缓存记录，再创建正式对象，确保后续可回填创建出的 datumPointIds。
      this.createDatumPointsFromPreview(featureId, this.state.preview.points);
    }
    this.options.onCommitted?.(definition, this.state.preview);
    this.editSession = null;
    this.close();
    this.viewer.entitySelectionManager?.clearSelectionProgrammatically();
    this.viewer.setMode(exitViewerMode as never);
    this.viewer.requestRender();
    return { ok: true, featureId };
  }

  /**
   * 按对象 ID 解析基准点选择候选。
   *
   * @param objectId 场景对象 ID。
   * @returns 选择候选；不支持时返回 `null`。
   */
  public resolveSelectionCandidateByObjectId(objectId: string): PointSelectionCandidate | null {
    const datumPlaneId = parseDatumReferenceEntityId(objectId, DatumReferenceEntityKind.Plane);
    if (datumPlaneId !== null) {
      const datumPlane = this.viewer.getDatumPlane(datumPlaneId);
      return datumPlane ? this.buildPointCandidateFromDatumPlane(datumPlane) : null;
    }

    const datumAxisId = parseDatumReferenceEntityId(objectId, DatumReferenceEntityKind.Axis);
    if (datumAxisId !== null) {
      const datumAxis = this.viewer.getDatumAxis(datumAxisId);
      return datumAxis ? this.buildPointCandidateFromDatumAxis(datumAxis) : null;
    }

    const datumPointId = parseDatumReferenceEntityId(objectId, DatumReferenceEntityKind.Point);
    if (datumPointId !== null) {
      const datumPoint = this.viewer.getDatumPoint(datumPointId);
      return datumPoint ? this.buildPointCandidateFromDatumPoint(datumPoint) : null;
    }

    const object = this.viewer.getSceneObjectById(objectId);
    if (!object) {
      return null;
    }

    return this.resolveSelectionCandidateByObject(object, objectId);
  }

    /**
   * 按实体 ID 获取当前求解候选。
   *
   * @param entityId 实体 ID。
   * @returns 求解候选；不存在时返回 null。
   */
  public getSolveCandidateById(entityId: string): PointFeatureSolveRuntimeCandidate | null {
    const sourceSelections = this.solvingSelectionScope ?? this.state.selections;
    for (const list of Object.values(sourceSelections)) {
      const found = list.find((item) => item.entityId === entityId);
      if (found) {
        return {
          entityId: found.entityId,
          entityType: found.entityType,
          payload: found.payload,
        };
      }
    }
    return null;
  }

    /**
   * 解析单个引用实体。
   *
   * @param reference 引用实体。
   * @returns 已解析几何实体；不存在时返回 null。
   */
  public resolveReference(reference: PointEntityReference) {

        const topologyCandidate = this.resolveTopologyPriorityPointSelectionCandidate(reference);
    const candidate = topologyCandidate ?? this.getSolveCandidateById(reference.entityId);

        if (!candidate) {
      return null;
    }
    return {
      id: candidate.entityId,
      type: candidate.entityType,
      data: {
        ...(candidate.payload ?? {}),
        assemblyTopologyRef: reference.assemblyTopologyRef ?? readPointSelectionAssemblyTopologyRef(candidate.payload),
        topologyPick: reference.topologyPick ?? readPointSelectionTopologyPick(candidate.payload),
      },
    };
  }

    /**
   * 捕获基准点命令与正式对象历史快照。
   *
   * 设计说明：
   * 1. `DatumPointManager` 只知道正式点对象，不知道点由哪些引用求解而来；
   * 2. 控制器持有 `PointFeatureRecord`，这是装配体移动后重算的唯一可复算定义来源；
   * 3. 因此历史快照在控制器边界汇总 manager 快照与特征记录，保证 Undo/Redo 后对象状态与重算链路一致。
   *
   * @returns 基准点历史快照。
   */
  public createHistorySnapshot(): DatumPointHistorySnapshot {
    const managerSnapshot = this.viewer.datumPointManager?.createHistorySnapshot() ?? {
      managerId: DATUM_POINT_HISTORY_ENTITY_ID,
      points: [],
      features: [],
    };

    return {
      ...managerSnapshot,
      features: this.records.map((record) => this.createHistoryFeatureRecord(record)),
    };
  }

  /**
   * 恢复基准点引用对应的当前选择候选。
   *
   * @param reference 基准点实体引用。
   * @returns 当前可用选择候选；无法恢复时返回 `null`。
   */
  private resolveRestoredPointSelectionCandidate(reference: PointEntityReference): PointSelectionCandidate | null {
    return this.resolveTopologyPriorityPointSelectionCandidate(reference)
      ?? this.resolveSelectionCandidateByObjectId(reference.entityId);
  }

    /**
   * 进入特征编辑态。
   *
   * @param featureId 特征 ID。
   * @returns 是否进入成功。
   */
  public editDefinition(featureId: string): boolean {
    const found = this.records.find((item) => item.featureId === featureId);
    if (!found) {
      return false;
    }
    this.pendingCreationContext = found.creationContext;

    this.state = {
      ...createEmptyCommandState(),
      lifecycle: ReferenceCommandLifecycle.Open,
      placementDriver: found.definition.placementDriver,
      geometryMethod: found.definition.geometryMethod,
      modeSource: ReferenceCommandModeSource.ManualLocked,
      params: cloneParams(found.definition.params),
      selections: this.rehydrateSelections(found.definition),
      dirty: false,
    };

    this.recomputePreview();
    return true;
  }

  /**
   * 同步基准点当前选择的可视状态。
   */
  public syncSelectionVisuals(): void {
    const selectedObjects = Object.values(this.state.selections)
      .flat()
      .map((item) => this.resolveSelectionObject(item.entityId))
      .filter((object): object is THREE.Object3D => object !== null);
    this.viewer.entitySelectionManager?.updateSelectionState(
      selectedObjects.map((object) => ({
        distance: 0,
        object,
        point: object.getWorldPosition(new THREE.Vector3()),
      })),
    );
    this.viewer.requestRender();
  }

  /**
   * 构建正式基准点候选。
   *
   * @param datumPoint 正式基准点。
   * @returns 基准点候选。
   */
  private buildPointCandidateFromDatumPoint(datumPoint: { datumPointId: number; name: string; getWorldPoint(): THREE.Vector3 }): PointSelectionCandidate {
    const datumPointId = datumPoint.datumPointId;
    return {
      entityId: buildDatumReferenceEntityId(DatumReferenceEntityKind.Point, datumPointId),
      entityType: ReferenceEntitySemanticType.DatumPoint,
      label: datumPoint.name || buildDatumReferenceEntityId(DatumReferenceEntityKind.Point, datumPointId),
      payload: {
        point: datumPoint.getWorldPoint(),
      },
    };
  }

  /**
   * 构建正式基准轴候选。
   *
   * @param datumAxis 正式基准轴。
   * @returns 基准轴候选。
   */
  private buildPointCandidateFromDatumAxis(datumAxis: {
    datumAxisId: number;
    name: string;
    getWorldEndpoints(): LineSegmentEndpoints | null;
    getWorldMidpoint(): THREE.Vector3 | null;
    getWorldDirection(): THREE.Vector3;
  }): PointSelectionCandidate | null {
    const endpoints = datumAxis.getWorldEndpoints();
    if (!endpoints) {
      return null;
    }

    const normalizedEndpoints = normalizeLinearEndpointsDirection(this.viewer, endpoints);
    const datumAxisId = datumAxis.datumAxisId;
    return {
      entityId: buildDatumReferenceEntityId(DatumReferenceEntityKind.Axis, datumAxisId),
      entityType: ReferenceEntitySemanticType.DatumAxis,
      label: datumAxis.name || buildDatumReferenceEntityId(DatumReferenceEntityKind.Axis, datumAxisId),
      payload: {
        startPoint: normalizedEndpoints.start,
        endPoint: normalizedEndpoints.end,
        point: datumAxis.getWorldMidpoint(),
        direction: datumAxis.getWorldDirection(),
      },
    };
  }

  /**
   * 构建正式基准面候选。
   *
   * @param datumPlane 正式基准面。
   * @returns 基准面候选。
   */
  private buildPointCandidateFromDatumPlane(datumPlane: {
    id: number;
    name: string;
    normal: THREE.Vector3;
    originWorld: THREE.Vector3;
  }): PointSelectionCandidate {
    return {
      entityId: buildDatumReferenceEntityId(DatumReferenceEntityKind.Plane, datumPlane.id),
      entityType: ReferenceEntitySemanticType.DatumPlane,
      label: datumPlane.name || buildDatumReferenceEntityId(DatumReferenceEntityKind.Plane, datumPlane.id),
      payload: {
        planeNormal: datumPlane.normal.clone(),
        planePoint: datumPlane.originWorld.clone(),
      },
    };
  }

  /**
   * 解析选择对应的场景对象。
   *
   * @param entityId 引用实体 ID。
   * @returns 场景对象；不存在时返回 `null`。
   */
  private resolveSelectionObject(entityId: string): THREE.Object3D | null {
    const datumPlaneId = parseDatumReferenceEntityId(entityId, DatumReferenceEntityKind.Plane);
    if (datumPlaneId !== null) {
      return this.viewer.getDatumPlane(datumPlaneId)?.getObject3D() ?? null;
    }

    const datumAxisId = parseDatumReferenceEntityId(entityId, DatumReferenceEntityKind.Axis);
    if (datumAxisId !== null) {
      return this.viewer.getDatumAxis(datumAxisId);
    }

    const datumPointId = parseDatumReferenceEntityId(entityId, DatumReferenceEntityKind.Point);
    if (datumPointId !== null) {
      return this.viewer.getDatumPoint(datumPointId);
    }

    return this.viewer.getSceneObjectById(entityId);
  }

    /**
   * 重算装配体上下文创建的基准点。
   *
   * 设计说明：
   * 1. 装配体零部件移动或旋转后，依赖特征点、线、面创建的基准点必须重新求解；
   * 2. 基准点已经保存 `PointFeatureRecord.definition`，这里复用现有重建链路，避免引入第二套求解逻辑；
   * 3. 数值 XYZ 点不依赖装配体拓扑，虽然保留在装配体上下文中，也会通过原定义重建为同一位置。
   *
   * @returns 成功重算的基准点特征数量。
   */
  public recomputeAssemblyDatumPoints(): number {
    let recomputedCount = 0;

    this.records.forEach((record) => {
      if (record.creationContext !== ReferenceCreationContext.Assembly) {
        return;
      }
      if (this.rebuildFeature(record.featureId)) {
        recomputedCount += 1;
      }
    });

    if (recomputedCount > 0) {
      this.viewer.requestRender();
    }
    return recomputedCount;
  }

  /**
   * 按历史快照恢复基准点命令与正式对象状态。
   *
   * @param snapshot 基准点历史快照。
   */
  public restoreHistorySnapshot(snapshot: DatumPointHistorySnapshot): void {
    this.restoreEditingPointVisibilityIfNeeded();
    this.editSession = null;
    this.viewer.datumPointManager?.restoreHistorySnapshot(snapshot);
    this.records = (snapshot.features ?? []).map((record) => this.createPointFeatureRecordFromHistory(record));
    this.state = createEmptyCommandState();
    this.pendingCreationContext = ReferenceCreationContext.PartFeature;
    this.clearPreview();
    this.viewer.entitySelectionManager?.clearSelectionProgrammatically();
    this.viewer.requestRender();
  }

  /**
   * 从运行时特征记录创建历史记录。
   *
   * @param record 运行时特征记录。
   * @returns 可序列化历史记录。
   */
  private createHistoryFeatureRecord(record: PointFeatureRecord): DatumPointFeatureHistoryRecord {
    return {
      featureId: record.featureId,
      creationContext: record.creationContext,
      definition: this.clonePointFeatureDefinition(record.definition),
      status: record.resultCache.status,
      diagnostics: record.rebuildDiagnostics.map((diagnostic) => this.createHistoryDiagnostic(diagnostic)),
      points: record.points.map((point) => this.createHistoryVector(point)),
      visible: record.visible,
      datumPointIds: [...(record.datumPointIds ?? [])],
    };
  }

  /**
   * 从历史记录恢复运行时特征记录。
   *
   * @param record 历史特征记录。
   * @returns 运行时特征记录。
   */
  private createPointFeatureRecordFromHistory(record: DatumPointFeatureHistoryRecord): PointFeatureRecord {
    const points = record.points.map((point) => this.createVectorFromHistory(point));

    return {
      featureId: record.featureId,
      creationContext: record.creationContext,
      definition: this.clonePointFeatureDefinition(record.definition),
      resultCache: {
        points,
        status: record.status,
        diagnostics: record.diagnostics.map((diagnostic) => ({ ...diagnostic })),
      },
      rebuildDiagnostics: record.diagnostics.map((diagnostic) => ({ ...diagnostic })),
      visible: record.visible,
      points,
      datumPointIds: [...(record.datumPointIds ?? [])],
    };
  }

  /**
   * 克隆基准点特征定义。
   *
   * @param definition 特征定义。
   * @returns 克隆后的特征定义。
   */
  private clonePointFeatureDefinition(definition: PointFeatureDefinition): PointFeatureDefinition {
    return JSON.parse(JSON.stringify(definition)) as PointFeatureDefinition;
  }

  /**
   * 创建历史诊断记录。
   *
   * @param diagnostic 运行时诊断。
   * @returns 可序列化诊断记录。
   */
  private createHistoryDiagnostic(diagnostic: PointFeatureDiagnostic): DatumPointHistoryDiagnostic {
    return {
      code: diagnostic.code,
      message: diagnostic.message,
      severity: diagnostic.severity,
    };
  }

  /**
   * 创建历史向量。
   *
   * @param vector Three.js 向量。
   * @returns plain data 向量。
   */
  private createHistoryVector(vector: THREE.Vector3): DatumPointHistoryVector3 {
    return [vector.x, vector.y, vector.z];
  }

  /**
   * 从历史向量创建 Three.js 向量。
   *
   * @param vector 历史向量。
   * @returns Three.js 向量。
   */
  private createVectorFromHistory(vector: DatumPointHistoryVector3): THREE.Vector3 {
    return new THREE.Vector3(vector[0], vector[1], vector[2]);
  }

    /**
   * 按场景对象解析基准点选择候选。
   *
   * 设计说明：
   * 1. 普通选择可通过 uuid 从场景查回对象；
   * 2. 拓扑代理可能由拓扑仓库按需返回，不一定能再通过 uuid 查回；
   * 3. 因此对象级候选构造必须独立出来，供普通选择与拓扑优先解析共用。
   *
   * @param object 场景对象或拓扑代理对象。
   * @param entityIdOverride 可选实体 ID 覆盖值。
   * @returns 选择候选；不支持时返回 `null`。
   */
  private resolveSelectionCandidateByObject(
    object: THREE.Object3D,
    entityIdOverride?: string,
  ): PointSelectionCandidate | null {
    const entityId = entityIdOverride ?? object.uuid;

    if (isDatumPoint(object)) {
      return this.buildPointCandidateFromDatumPoint(object);
    }

    if (isFeaturePoint(object)) {
      return {
        entityId,
        entityType: ReferenceEntitySemanticType.FeaturePoint,
        label: object.name || entityId,
        payload: {
          ...buildPointSelectionTopologyPayload(object),
          point: object.getWorldPoint(),
        },
      };
    }

    if (isFeatureLine(object)) {

        if (isFeatureCurve(object) && object.curveKind === 'circle') {
        const circularEdgePayload = resolveCircularEdgePayload(object);
        if (circularEdgePayload) {
          return {
            entityId,
            entityType: ReferenceEntitySemanticType.FeatureCurve,
            label: object.name || entityId,
            payload: {
              ...buildPointSelectionTopologyPayload(object),
              ...circularEdgePayload,
            },
          };
        }
      }


        const endpoints = object.getWorldEndpoints();
      if (!endpoints) {
        return null;
      }

        /**
       * 参考点在线上的默认距离方向必须稳定为“左到右”。
       * 这里先对线段端点做归一化，再把结果写入求解上下文，避免底层几何顶点顺序影响 Along Curve 的自然方向。
       */
      const normalizedEndpoints = normalizeLinearEndpointsDirection(this.viewer, endpoints);

        return {
        entityId,
        entityType: isFeatureCurve(object)
          ? ReferenceEntitySemanticType.FeatureCurve
          : ReferenceEntitySemanticType.FeatureLine,
        label: object.name || entityId,
        payload: {
          ...buildPointSelectionTopologyPayload(object),
          startPoint: normalizedEndpoints.start,
          endPoint: normalizedEndpoints.end,
        },
      };
    }


        if (isDatumAxis(object)) {
      return this.buildPointCandidateFromDatumAxis(object);
    }


        const selectedDatumPlaneId = getDatumPlaneIdFromObject(object);
    if (selectedDatumPlaneId !== null) {
      const datumPlane = this.viewer.getDatumPlane(selectedDatumPlaneId);
      return datumPlane ? this.buildPointCandidateFromDatumPlane(datumPlane) : null;
    }


        if (isFeatureFace(object)) {
      if (isFeatureSurface(object) && object.surfaceKind !== 'plane') {
        return {
          entityId,
          entityType: ReferenceEntitySemanticType.FeatureSurface,
          label: object.name || entityId,
          payload: {
            ...buildPointSelectionTopologyPayload(object),
            centroid: object.getWorldPoint(),
            surfaceObject: object,
            surfaceKind: object.surfaceKind,
          },
        };
      }

      const plane = object.getWorldPlane();
      return {
        entityId,
        entityType: ReferenceEntitySemanticType.FeatureFace,
        label: object.name || entityId,
        payload: {
          ...buildPointSelectionTopologyPayload(object),
          centroid: object.getWorldPoint(),
          planeNormal: plane.normal,
          planePoint: plane.point,
        },
      };
    }

    return null;
  }

  /**
   * 重建指定特征。
   *
   * @param featureId 特征 ID。
   * @returns 是否重建成功。
   */
  public rebuildFeature(featureId: string): boolean {
    const found = this.records.find((item) => item.featureId === featureId);
    if (!found) {
      return false;
    }
    const preview = this.solvePreview(found.definition, this.rehydrateSelections(found.definition));
    found.resultCache = preview;
    found.rebuildDiagnostics = preview.diagnostics;
    found.points = preview.points;
    this.rebuildDatumPointRecord(found);
    return true;
  }

    /**
   * 按拓扑引用优先恢复基准点选择候选。
   *
   * 设计说明：
   * 1. 基准点求解依赖候选 payload，因此必须在进入 solver 前刷新拓扑代理；
   * 2. 旧 `entityId` 对应的对象即使仍存在，也不能优先于 `topologyPick` 或 `assemblyTopologyRef`；
   * 3. 该函数只服务基准点控制器，不改变点/线/面公共几何读取函数。
   *
   * @param reference 基准点实体引用。
   * @returns 拓扑恢复出的选择候选；无法恢复时返回 `null`。
   */
  private resolveTopologyPriorityPointSelectionCandidate(reference: PointEntityReference): PointSelectionCandidate | null {
    if (reference.assemblyTopologyRef) {
      const assemblyTopologyObject = resolveAssemblyTopologyReferenceObject(this.viewer, reference.assemblyTopologyRef);
      const assemblyTopologyCandidate = assemblyTopologyObject
        ? this.resolveSelectionCandidateByObject(assemblyTopologyObject)
        : null;
      if (assemblyTopologyCandidate) {
        return assemblyTopologyCandidate;
      }
    }

    if (!reference.topologyPick) {
      return null;
    }

    const topologyObject = this.resolvePointReferenceObjectFromTopologyPick(reference.topologyPick);
    return topologyObject
      ? this.resolveSelectionCandidateByObject(topologyObject)
      : null;
  }

    /**
   * 基于预览结果创建正式基准点。
   *
   * @param featureId 特征 ID。
   * @param points 点集合。
   */
  private createDatumPointsFromPreview(featureId: string, points: THREE.Vector3[]): void {
    if (points.length === 0) {
      return;
    }
    const record = this.records.find((item) => item.featureId === featureId);
    const createdPointIds: number[] = [];
    points.forEach((point) => {
      const datumPoint = this.viewer.datumPointManager.createPoint({
        creationContext: record?.creationContext ?? this.pendingCreationContext,
        name: '',
        position: point.clone(),
      });
      if (datumPoint) {
        createdPointIds.push(datumPoint.datumPointId);
      }
    });
    if (record && createdPointIds.length > 0) {
      record.datumPointIds = createdPointIds;
    }
  }


    /**
   * 用预览结果更新编辑目标对应的正式基准点集合。
   *
   * @param record 编辑目标特征记录。
   * @param pointStates 编辑前正式点状态快照。
   * @returns 更新成功返回 `true`。
   */
  private updateDatumPointsFromPreviewForEdit(
    record: PointFeatureRecord,
    pointStates: ReferencePointEditSessionPointState[],
  ): boolean {
    const previousPointIds = pointStates.map((state) => state.pointId);
    const nextPointIds: number[] = [];

    if (record.points.length === 0) {
      previousPointIds.forEach((pointId) => {
        this.viewer.datumPointManager.removePoint(pointId);
      });
      record.datumPointIds = [];
      return true;
    }

    record.points.forEach((point, index) => {
      const state = pointStates[index] ?? null;
      const existingPointId = state?.pointId;
      const existingPoint = typeof existingPointId === 'number'
        ? this.viewer.getDatumPoint(existingPointId)
        : null;
      const nextVisible = state?.previousVisible ?? record.visible;
      const nextName = state?.pointName?.trim() ?? '';

      if (existingPoint) {
        existingPoint.updatePosition(point);
        existingPoint.setVisible(nextVisible);
        if (nextName.length > 0) {
          existingPoint.setName(nextName);
        }
        nextPointIds.push(existingPoint.datumPointId);
        return;
      }

      const recreatedPoint = this.viewer.datumPointManager.createPoint({
        id: existingPointId,
        creationContext: record.creationContext,
        name: nextName,
        position: point.clone(),
        visible: nextVisible,
      });
      nextPointIds.push(recreatedPoint.datumPointId);
    });

    previousPointIds.slice(record.points.length).forEach((pointId) => {
      this.viewer.datumPointManager.removePoint(pointId);
    });

    record.datumPointIds = nextPointIds;
    record.visible = nextPointIds.some((pointId) => this.viewer.getDatumPoint(pointId)?.isVisible() === true);
    return true;
  }

  /**
   * 若当前存在编辑会话，恢复被临时隐藏的正式基准点可见性。
   */
  private restoreEditingPointVisibilityIfNeeded(): void {
    const activeEditSession = this.editSession;
    if (!activeEditSession) {
      return;
    }
    activeEditSession.pointStates.forEach((pointState) => {
      this.viewer.getDatumPoint(pointState.pointId)?.setVisible(pointState.previousVisible);
    });
  }


    /**
   * 重建正式基准点可视对象。
   *
   * @param record 特征记录。
   */
  private rebuildDatumPointRecord(record: PointFeatureRecord): void {
    const previousPointIds = [...(record.datumPointIds ?? [])];
    const nextPointIds: number[] = [];
    if (record.points.length === 0) {
      previousPointIds.forEach((pointId) => {
        this.viewer.datumPointManager.removePoint(pointId);
      });
      record.datumPointIds = [];
      return;
    }

    record.points.forEach((point, index) => {
      const existingPointId = previousPointIds[index];
      const existingPoint = typeof existingPointId === 'number'
        ? this.viewer.getDatumPoint(existingPointId)
        : null;
      if (existingPoint) {
        existingPoint.updatePosition(point);
        existingPoint.setVisible(record.visible);
        nextPointIds.push(existingPoint.datumPointId);
        return;
      }

      const datumPoint = this.viewer.datumPointManager.createPoint({
        creationContext: record.creationContext,
        name: '',
        position: point.clone(),
      });
      if (datumPoint) {
        datumPoint.setVisible(record.visible);
        nextPointIds.push(datumPoint.datumPointId);
      }
    });

    previousPointIds.slice(record.points.length).forEach((pointId) => {
      this.viewer.datumPointManager.removePoint(pointId);
    });
    record.datumPointIds = nextPointIds;
  }

  /**
   * 创建基准点特征 ID。
   *
   * @returns 唯一特征 ID。
   */
  private createPointFeatureId(): string {
    return `datum-point:${Date.now()}:${Math.random().toString(36).slice(2, 10)}`;
  }

  /**
   * 尝试自动切换几何方法。
   *
   * @param candidate 新选择候选。
   * @returns 是否完成自动切换。
   */
  private tryAutoModeTransition(candidate: PointSelectionCandidate): boolean {
    if (this.state.geometryMethod === PointGeometryMethod.CenterOfFace && this.state.modeSource === ReferenceCommandModeSource.ManualLocked) {
      return false;
    }
    if (this.tryAutoTransitionToIntersection(candidate)) {
      return true;
    }
    if (this.tryAutoTransitionToProjection(candidate)) {
      return true;
    }
    return false;
  }

  /**
   * 尝试自动切换到投影模式。
   *
   * @param candidate 新选择候选。
   * @returns 是否切换成功。
   */
  private tryAutoTransitionToProjection(candidate: PointSelectionCandidate): boolean {
    if (this.state.geometryMethod === PointGeometryMethod.OnPoint && this.isFaceSelectionCandidate(candidate)) {
      const point = this.state.selections.point?.[0];
      if (!point) {
        return false;
      }
      this.state.geometryMethod = PointGeometryMethod.Projection;
      this.state.modeSource = ReferenceCommandModeSource.AutoInferred;
      this.state.selections = { source: [point], target: [candidate] };
      return true;
    }

    if (this.state.geometryMethod === PointGeometryMethod.CenterOfFace && this.isPointSelectionCandidate(candidate)) {
      const face = this.state.selections.face?.[0];
      if (!face) {
        return false;
      }
      this.state.geometryMethod = PointGeometryMethod.Projection;
      this.state.modeSource = ReferenceCommandModeSource.AutoInferred;
      this.state.selections = { source: [candidate], target: [face] };
      return true;
    }

    return false;
  }

  /**
   * 尝试自动切换到交点模式。
   *
   * @param candidate 新选择候选。
   * @returns 是否切换成功。
   */
  private tryAutoTransitionToIntersection(candidate: PointSelectionCandidate): boolean {
    if (!this.isIntersectionTriggerCandidate(candidate)) {
      return false;
    }

    if (this.state.geometryMethod === PointGeometryMethod.AlongCurve) {
      const first = this.state.selections.curve?.[0];
      if (!first) {
        return false;
      }
      this.lastAlongCurveParams = cloneParams(this.state.params).alongCurve;
      this.state.geometryMethod = PointGeometryMethod.Intersection;
      this.state.modeSource = ReferenceCommandModeSource.AutoInferred;
      this.state.selections = { entityA: [first], entityB: [candidate] };
      return true;
    }

    if (this.state.geometryMethod === PointGeometryMethod.CircularCenter) {
      const first = this.state.selections.primary?.[0];
      if (!first || !this.isIntersectionTriggerCandidate(first)) {
        return false;
      }
      this.state.geometryMethod = PointGeometryMethod.Intersection;
      this.state.modeSource = ReferenceCommandModeSource.AutoInferred;
      this.state.selections = { entityA: [first], entityB: [candidate] };
      return true;
    }

    return false;
  }

  /**
   * 判断候选是否为线性实体。
   *
   * @param candidate 选择候选。
   * @returns 是否为线性实体。
   */
  private isLinearSelectionCandidate(candidate: PointSelectionCandidate): boolean {
    return candidate.entityType === ReferenceEntitySemanticType.FeatureLine
      || candidate.entityType === ReferenceEntitySemanticType.FeatureCurve
      || candidate.entityType === ReferenceEntitySemanticType.DatumAxis;
  }

  /**
   * 判断候选是否为圆形实体。
   *
   * @param candidate 选择候选。
   * @returns 是否为圆形实体。
   */
  private isCircularSelectionCandidate(candidate: PointSelectionCandidate): boolean {
    return candidate.entityType === ReferenceEntitySemanticType.FeatureCurve
      && candidate.payload?.center !== undefined;
  }

  /**
   * 判断候选是否可以触发交点模式。
   *
   * @param candidate 选择候选。
   * @returns 是否可触发交点模式。
   */
  private isIntersectionTriggerCandidate(candidate: PointSelectionCandidate): boolean {
    return this.isLinearSelectionCandidate(candidate) || this.isCircularSelectionCandidate(candidate);
  }

  /**
   * 判断在交点模式下是否保留当前模式。
   *
   * @param candidate 新选择候选。
   * @returns 是否保持交点模式。
   */
  private shouldPreserveIntersectionModeOnSelection(candidate: PointSelectionCandidate): boolean {
    if (this.state.geometryMethod !== PointGeometryMethod.Intersection) {
      return false;
    }
    const hasEntityA = (this.state.selections.entityA?.length ?? 0) > 0;
    const hasEntityB = (this.state.selections.entityB?.length ?? 0) > 0;
    if (!hasEntityA || !hasEntityB) {
      return false;
    }
    return this.isIntersectionTriggerCandidate(candidate);
  }

  /**
   * 判断候选是否为面实体。
   *
   * @param candidate 选择候选。
   * @returns 是否为面实体。
   */
  private isFaceSelectionCandidate(candidate: PointSelectionCandidate): boolean {
    return candidate.entityType === ReferenceEntitySemanticType.FeatureFace
      || candidate.entityType === ReferenceEntitySemanticType.DatumPlane
      || candidate.entityType === ReferenceEntitySemanticType.FeatureSurface;
  }

  /**
   * 判断当前是否应优先保持投影模式，而不是切换到在点上。
   *
   * @param candidate 新选择候选。
   * @returns 是否应保持投影链路。
   */
  private shouldPreferProjectionOverOnPoint(candidate: PointSelectionCandidate): boolean {
    if (!this.isPointSelectionCandidate(candidate)) {
      return false;
    }

    /**
     * 当前只要已经存在投影目标（正式基准面、结果体平面面、非平面曲面），
     * 再选点时都必须继续留在 Projection 链路，不能被点优先逻辑抢回 OnPoint。
     */
    return Object.values(this.state.selections)
      .flat()
      .some((item) => (
        item.entityType === ReferenceEntitySemanticType.DatumPlane
        || item.entityType === ReferenceEntitySemanticType.FeatureFace
        || item.entityType === ReferenceEntitySemanticType.FeatureSurface
      ));
  }

  /**
   * 判断候选是否为点性实体。
   *
   * @param candidate 选择候选。
   * @returns 是否为点性实体。
   */
  private isPointSelectionCandidate(candidate: PointSelectionCandidate): boolean {
    return candidate.entityType === ReferenceEntitySemanticType.FeaturePoint
      || candidate.entityType === ReferenceEntitySemanticType.DatumPoint;
  }

  /**
   * 删除选择后应用模式回退。
   */
  private applyModeFallbackAfterDeletion(): void {
    this.applyProjectionFallbackAfterDeletion();
    this.applyIntersectionFallbackAfterDeletion();
  }

  /**
   * 投影模式删除后尝试回退为更简单的单参考模式。
   */
  private applyProjectionFallbackAfterDeletion(): void {
    if (this.state.geometryMethod !== PointGeometryMethod.Projection) {
      return;
    }

    const remaining = Object.values(this.state.selections).flat();

      const face = remaining.find((item) => (
      item.entityType === ReferenceEntitySemanticType.FeatureFace
      || item.entityType === ReferenceEntitySemanticType.FeatureSurface
    ));

      const plane = remaining.find((item) => item.entityType === ReferenceEntitySemanticType.DatumPlane);
    const point = remaining.find((item) => this.isPointSelectionCandidate(item));

    if (face && !point) {
      this.state.geometryMethod = PointGeometryMethod.CenterOfFace;
      this.state.modeSource = ReferenceCommandModeSource.AutoInferred;
      this.state.selections = { face: [face] };
      return;
    }

    if (point && !face && !plane) {
      this.state.geometryMethod = PointGeometryMethod.OnPoint;
      this.state.modeSource = ReferenceCommandModeSource.AutoInferred;
      this.state.selections = { point: [point] };
    }
  }

  /**
   * 交点模式删除后尝试回退为沿曲线模式。
   */
  private applyIntersectionFallbackAfterDeletion(): void {
    if (this.state.geometryMethod !== PointGeometryMethod.Intersection) {
      return;
    }
    const remaining = Object.values(this.state.selections).flat();
    if (remaining.length !== 1) {
      return;
    }
    const remainingCandidate = remaining[0];
    if (!this.isLinearSelectionCandidate(remainingCandidate)) {
      return;
    }
    this.state.geometryMethod = PointGeometryMethod.AlongCurve;
    this.state.modeSource = ReferenceCommandModeSource.AutoInferred;
    this.state.selections = { curve: [remainingCandidate] };
    this.state.params = {
      ...this.state.params,
      alongCurve: this.lastAlongCurveParams ?? {
        ...this.state.params.alongCurve,
        distributionType: PointAlongCurveDistributionType.Distance,
        spacingValue: ReferencePointController.ALONG_CURVE_DISTANCE_DEFAULT,
        pointCount: 1,
      },
    };
    this.lastAlongCurveParams = null;
  }

  /**
   * 构建当前状态对应的特征定义。
   *
   * @param featureId 特征 ID。
   * @returns 序列化后的特征定义。
   */
  private buildDefinition(featureId: string): PointFeatureDefinition {
    return {
      id: featureId,
      placementDriver: this.state.placementDriver,
      geometryMethod: this.state.geometryMethod,
      references: buildGeometryReferences(this.state.selections, this.state.geometryMethod),
      params: cloneParams(this.state.params),
      policies: this.policies,
      context: {
        documentId: this.options.documentId,
        coordinateFrame: this.options.coordinateFrame ?? ReferenceCoordinateFrame.Part,
        unitSystem: this.options.unitSystem ?? 'm',
      },
    };
  }

  /**
   * 根据定义恢复选择状态。
   *
   * @param definition 特征定义。
   * @returns 恢复后的选择映射。
   */
  private rehydrateSelections(definition: PointFeatureDefinition): PointCommandState['selections'] {
    const selections: PointCommandState['selections'] = {};
    definition.references.forEach((item) => {
      const list = selections[item.slot] ?? [];

        const refreshedCandidate = this.resolveRestoredPointSelectionCandidate(item);

        /**
       * 重建时必须重新读取当前场景几何载荷。
       *
       * 说明：
       * 1. 基准点求解器依赖候选 payload 中的点、线、面几何；
       * 2. 这些 payload 是创建时的快照，零部件移动或旋转后不能继续复用旧值；
       * 3. 因此这里按引用 ID 重新解析候选，并只保留原定义中的槽位与实体类型。
       */
      list.push({
        entityId: refreshedCandidate ? refreshedCandidate.entityId : item.entityId,
        entityType: item.entityType as PointSelectionCandidate['entityType'],
        label: refreshedCandidate?.label ?? item.entityId,
        payload: {
          ...(refreshedCandidate?.payload ?? {}),
          assemblyTopologyRef: item.assemblyTopologyRef ?? readPointSelectionAssemblyTopologyRef(refreshedCandidate?.payload),

            // 还原拓扑命中快照，供重算或二次编辑链路使用
          topologyPick: item.topologyPick ?? readPointSelectionTopologyPick(refreshedCandidate?.payload),

        },
      });
      selections[item.slot] = list;
    });
    return selections;
  }

  /**
   * 判断指定实体是否已存在于当前选择中。
   *
   * @param entityId 实体 ID。
   * @returns 已存在返回 `true`。
   */
  private hasSelectionCandidate(entityId: string): boolean {
    return Object.values(this.state.selections)
      .flat()
      .some((item) => item.entityId === entityId);
  }

  /**
   * 插入一个选择候选。
   *
   * @param candidate 选择候选。
   * @returns 是否插入成功。
   */
  private insertSelection(candidate: PointSelectionCandidate): boolean {
    if (!this.state.geometryMethod) {
      return false;
    }

    if (this.state.geometryMethod === PointGeometryMethod.CircularCenter && !this.isCircularSelectionCandidate(candidate)) {
      return false;
    }

    if (this.state.geometryMethod === PointGeometryMethod.Intersection) {
      const entityA = this.state.selections.entityA?.[0] ?? null;
      const entityB = this.state.selections.entityB?.[0] ?? null;
      if (!this.isIntersectionTriggerCandidate(candidate)) {
        return false;
      }
      if (!entityA) {
        this.state.selections.entityA = [candidate];
        return true;
      }
      if (!entityB) {
        this.state.selections.entityB = [candidate];
        return true;
      }
      this.state.selections.entityA = [entityB];
      this.state.selections.entityB = [candidate];
      return true;
    }

    const contract = getContract(this.state.geometryMethod);
    if (!contract) {
      return false;
    }

    for (const slot of contract.slots) {
      const current = this.state.selections[slot.slotId] ?? [];
      if (!slot.allowedTypes.includes(candidate.entityType)) {
        continue;
      }
      if (current.length >= slot.maxCount) {
        this.state.selections[slot.slotId] = [candidate];
        return true;
      }
      this.state.selections[slot.slotId] = [...current, candidate];
      return true;
    }

    return false;
  }

  /**
   * 求解当前预览。
   *
   * @param definition 预览定义。
   * @param selections 选择映射。
   * @returns 求解结果。
   */
  private solvePreview(definition: PointFeatureDefinition, selections: SlotSelectionMap): PointFeatureResult {
    this.solvingSelectionScope = selections;
    try {
      return this.solver.solve(definition);
    } finally {
      this.solvingSelectionScope = null;
    }
  }


    /**
   * 同步预览可视对象。
   *
   * @param preview 预览结果。
   */
  private syncPreviewVisualization(preview: PointFeatureResult | undefined): void {
    if (!preview || preview.status !== PointFeatureStatus.Ready || preview.points.length === 0) {
      this.clearPreview();
      return;
    }

    // 预览层必须直接消费求解结果中的全部点位，保证 Along Curve 多点预览与提交结果一致。
    this.previewRenderer.render(preview.points);
  }

  /**
   * 清理预览可视对象。
   */
  private clearPreview(): void {
    this.previewRenderer.clear();
  }


    /**
   * 重新计算当前预览结果。
   */
  private recomputePreview(): void {
    this.state.selectionState = updateSelectionState(this.state);

    if (this.state.lifecycle !== ReferenceCommandLifecycle.Open) {
      this.state.solveState = ReferenceSolveState.Idle;
      this.state.preview = undefined;

        this.clearPreview();

        this.viewer.requestRender();
      return;
    }

    this.state.solveState = ReferenceSolveState.Solving;
    const definition = this.buildDefinition('__preview__');
    // 所有 UI 回显都以统一的预览定义为输入，确保预览与提交逻辑完全一致。
    const preview = this.solvePreview(definition, this.state.selections);
    this.state.preview = preview;

    if (preview.status === PointFeatureStatus.Ready) {
      this.state.solveState = ReferenceSolveState.Previewable;
    } else if (preview.status === PointFeatureStatus.Ambiguous) {
      this.state.solveState = ReferenceSolveState.Ambiguous;
    } else if (preview.status === PointFeatureStatus.Incomplete) {
      this.state.solveState = ReferenceSolveState.Idle;
    } else {
      this.state.solveState = ReferenceSolveState.Error;
    }

    this.syncPreviewVisualization(preview);
    this.viewer.requestRender()

    const firstPoint = preview.points[0];
    if (
      preview.points.length > 0
      && firstPoint instanceof THREE.Vector3
    ) {
      const nextX = firstPoint.x;
      const nextY = firstPoint.y;
      const nextZ = firstPoint.z;
      const currentX = this.state.params.xyz?.x ?? 0;
      const currentY = this.state.params.xyz?.y ?? 0;
      const currentZ = this.state.params.xyz?.z ?? 0;
      const changed =
        Math.abs(nextX - currentX) > ReferencePointController.XYZ_SYNC_EPSILON
        || Math.abs(nextY - currentY) > ReferencePointController.XYZ_SYNC_EPSILON
        || Math.abs(nextZ - currentZ) > ReferencePointController.XYZ_SYNC_EPSILON;

      if (changed) {
        this.state.params = {
          ...this.state.params,
          xyz: {
            x: nextX,
            y: nextY,
            z: nextZ,
          },
        };
      }
    }

    this.viewer.requestRender();
  }


    /**
   * 判断基准点定义是否依赖特征结果体几何（即持有 topologyPick）。
   *
   * @param definition 基准点特征定义。
   * @returns 若任意引用持有 topologyPick 则返回 `true`。
   */
  private hasFeatureGeometryPointReference(definition: PointFeatureDefinition): boolean {
    if (!definition || !Array.isArray(definition.references)) {
      return false;
    }
    return definition.references.some((reference) => Boolean(reference.topologyPick));
  }

  /**
   * 根据拓扑命中快照解析当前结果体上的特征引用场景对象。
   *
   * @param topologyPick 拓扑命中快照。
   * @returns 场景对象；不存在时返回 `null`。
   */
  private resolvePointReferenceObjectFromTopologyPick(topologyPick: ResolvedTopologyPick): THREE.Object3D | null {
    return resolveModelTopologyProxyObject({
      scene: this.viewer.scene,
      topologyPick,
      getSceneObjectById: this.viewer.getSceneObjectById.bind(this.viewer),
    });
  }

  /**
   * 重算零件特征上下文中依赖特征几何的基准点。
   *
   * 设计说明：
   * 1. 特征 replay 会先清空并重建结果体，依赖特征面、边、点的基准点必须在结果体同步完成后复算；
   * 2. 默认基准点、无 topologyPick 的零件内基准点不参与本次重算；
   * 3. 复用既有 rebuildFeature 链路，避免引入第二套求解逻辑。
   *
   * @returns 成功重算的基准点数量。
   */
  public recomputePartFeatureDatumPoints(): number {
    let recomputedCount = 0;

    this.records.forEach((record) => {
      if (
        record.creationContext !== ReferenceCreationContext.PartFeature
        || !this.hasFeatureGeometryPointReference(record.definition)
      ) {
        return;
      }
      if (this.rebuildFeature(record.featureId)) {
        recomputedCount += 1;
      }
    });

    if (recomputedCount > 0) {
      this.viewer.requestRender();
    }
    return recomputedCount;
  }

}
